import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, IconButton, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import {
  EntreeJournalSuivi, EtatJalon, JalonSuivi, SuiviApplicable, TonPastille,
  formaterDateSuivi, libelleEntree, recupererSuiviDossier, statutJournal,
} from '../services/suiviClientServices';

// Bloc « Suivi client (site Inde tourisme) » de la fiche d'un dossier (lot 5 de l'espace client Inde tourisme),
// affiché par DemandesScreen sous « Informations du Formulaire » : l'étape que le client voit dans son espace
// (pastille, phrase, frise des 6 jalons), les mails de suivi de la veille du backend admin (journal) et la dernière
// connexion du client à son espace. Lecture seule : rien n'est écrit ni envoyé depuis ce bloc.
// Rend null (aucune trace) tant que la réponse n'est pas arrivée, pour un dossier qui n'est pas du site tourisme
// (applicable: false) et si le suivi est indisponible (backend pas encore redémarré, tables absentes, réseau, erreur) :
// la fiche des autres dossiers reste identique.

interface SuiviClientDossierProps {
  idDemande: number | null | undefined;
  // Change après une action sur le dossier (visa ou e-card déposés…) : le bloc se relit sans disparaître
  actualisation?: number;
}

// Couleurs du site Inde tourisme : pastilles de l'espace client
const TONS: Record<TonPastille, { fond: string; texte: string }> = {
  attente: { fond: '#FEF3DF', texte: '#8F5300' },
  ok: { fond: '#EEF6E4', texte: '#4E7A1E' },
  warn: { fond: '#FFF1E6', texte: '#B4471A' },
  ko: { fond: '#FEF3F2', texte: '#B42318' },
  neutre: { fond: '#EDF0F2', texte: '#55616A' },
};

// Ronds de la frise : vert fait, souci en cours, safran action requise, gris à venir, pointillé arrêté (refus, annulation)
const JALONS: Record<EtatJalon, { libelle: string; fond: string; bord: string; texte: string; pointille?: boolean }> = {
  fait: { libelle: 'fait', fond: '#82B440', bord: '#82B440', texte: '#1B1D1F' },
  actif: { libelle: 'en cours', fond: '#FFFFFF', bord: '#F4A836', texte: '#8F5300' },
  probleme: { libelle: 'action requise', fond: '#FFF1E6', bord: '#E8652D', texte: '#B4471A' },
  a_venir: { libelle: 'à venir', fond: '#FFFFFF', bord: '#D4DAE0', texte: '#5F6B76' },
  arrete: { libelle: 'arrêté', fond: '#EDF0F2', bord: '#9AA4AC', texte: '#5F6B76', pointille: true },
};

// Texte lu par les lecteurs d'écran seulement (état d'un jalon, porté visuellement par la couleur du rond)
const MASQUE_VISUEL = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden',
  clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
} as const;

// Mails affichés avant « Afficher les … plus anciens » (le journal en renvoie jusqu'à 50)
const MAX_MAILS_REPLIES = 5;

const Pastille: React.FC<{ texte: string; ton: TonPastille }> = ({ texte, ton }) => {
  const c = TONS[ton];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: '999px',
        bgcolor: c.fond, color: c.texte, fontSize: 13, fontWeight: 700, lineHeight: 1.3,
      }}
    >
      <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c.texte, flex: 'none' }} />
      {texte}
    </Box>
  );
};

// Mini-frise des jalons : 6 colonnes, 3 sur téléphone ; l'état complet au survol (title)
const Frise: React.FC<{ jalons: JalonSuivi[] }> = ({ jalons }) => (
  <Box
    component="ol"
    aria-label="Étapes vues par le client"
    sx={{
      listStyle: 'none', m: 0, mt: 1.5, p: 0, display: 'grid', columnGap: 0.5, rowGap: 1.5,
      gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: `repeat(${jalons.length}, minmax(0, 1fr))` },
    }}
  >
    {jalons.map((j, i) => {
      const s = JALONS[j.etat];
      return (
        <Box
          component="li"
          key={j.code || i}
          title={`${j.titre || j.court} : ${s.libelle}${j.meta ? ` (${j.meta})` : ''}`}
          sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 0 }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 26, height: 26, boxSizing: 'border-box', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: s.fond, border: `2px ${s.pointille ? 'dashed' : 'solid'} ${s.bord}`, color: s.texte,
              fontSize: 12, fontWeight: 700,
            }}
          >
            {j.etat === 'fait' ? <CheckIcon sx={{ fontSize: 16 }} /> : (j.etat === 'probleme' ? '!' : i + 1)}
          </Box>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5, fontWeight: 600, lineHeight: 1.2, overflowWrap: 'anywhere',
              color: j.etat === 'a_venir' || j.etat === 'arrete' ? 'text.secondary' : 'text.primary',
            }}
          >
            {j.court}
            <Box component="span" sx={MASQUE_VISUEL}>{` : ${s.libelle}`}</Box>
          </Typography>
          {j.meta && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
              {j.meta}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

// Une ligne du journal : date, libellé du mail (sujet au survol), statut en puce (explication au survol), détail
const LigneMail: React.FC<{ entree: EntreeJournalSuivi; derniere: boolean }> = ({ entree, derniere }) => {
  const statut = statutJournal(entree.statut);
  return (
    <Box component="li" sx={{ py: 0.75, borderBottom: derniere ? 0 : '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formaterDateSuivi(entree.date_creation) || 'date inconnue'}
        </Typography>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }} title={entree.sujet || undefined}>
          {libelleEntree(entree)}
        </Typography>
        <Chip
          label={statut.libelle}
          size="small"
          variant="outlined"
          color={statut.couleur}
          title={statut.aide || undefined}
          sx={{ height: 20, fontSize: 11 }}
        />
      </Box>
      {entree.detail && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
          {entree.detail}
        </Typography>
      )}
    </Box>
  );
};

const SuiviClientDossier: React.FC<SuiviClientDossierProps> = ({ idDemande, actualisation = 0 }) => {
  const [suivi, setSuivi] = useState<SuiviApplicable | null>(null);
  const [chargement, setChargement] = useState(false);
  const [echecActualisation, setEchecActualisation] = useState(false);
  const [relecture, setRelecture] = useState(0); // bouton « Actualiser »
  const [tousLesMails, setTousLesMails] = useState(false);
  // Dossier dont le suivi est à l'écran : changer de dossier efface le bloc, une relecture du même dossier le garde
  // affiché jusqu'à la réponse (pas de clignotement) et même si elle échoue
  const dossierAffiche = useRef<number | null>(null);

  useEffect(() => {
    let actif = true;
    const id = idDemande || null;
    const autreDossier = dossierAffiche.current !== id;
    if (autreDossier) {
      dossierAffiche.current = id;
      setSuivi(null);
      setTousLesMails(false);
    }
    setEchecActualisation(false);
    if (!id) {
      setChargement(false);
      return undefined;
    }
    setChargement(true);
    recupererSuiviDossier(id)
      .then((r) => {
        if (!actif) return;
        if (r) setSuivi(r.applicable ? r : null);
        else if (!autreDossier) setEchecActualisation(true); // suivi indisponible à la relecture : l'affichage reste
      })
      .catch((e) => {
        if (!actif) return;
        console.error('[SUIVI-CLIENT] Lecture du suivi du dossier impossible', e);
        if (!autreDossier) setEchecActualisation(true);
      })
      .finally(() => {
        if (actif) setChargement(false);
      });
    return () => { actif = false; };
  }, [idDemande, actualisation, relecture]);

  if (!suivi) return null;
  const { etape, journal } = suivi;
  const mailsVisibles = tousLesMails ? journal : journal.slice(0, MAX_MAILS_REPLIES);

  return (
    <Paper elevation={3} component="section" aria-label="Suivi client (site Inde tourisme)" sx={{ p: { xs: 1.5, sm: 2 }, mt: 2 }}>
      <Box sx={{ mb: 1.5, p: 1, bgcolor: '#e0f2f1', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography component="span" sx={{ fontWeight: 'bold', flex: 1, minWidth: 0 }}>
          Suivi client (site Inde tourisme)
        </Typography>
        <IconButton
          size="small"
          onClick={() => setRelecture((n) => n + 1)}
          disabled={chargement}
          aria-label="Actualiser le suivi client"
          title="Actualiser"
        >
          {chargement ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
        </IconButton>
      </Box>

      {suivi.dry_run && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <strong>Mode simulation : aucun mail ne part.</strong> Pour l'eVisa et l'e-Arrival Card, cochez « Envoyer un email
          au client » si le client doit être prévenu.
        </Alert>
      )}
      {echecActualisation && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
          Actualisation impossible pour le moment : informations du dernier chargement.
        </Typography>
      )}

      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.75 }}>
        Étape vue par le client :
      </Typography>
      <Pastille texte={etape.pastille.texte} ton={etape.pastille.ton} />
      {etape.phrase && (
        <Typography variant="body2" sx={{ mt: 1, px: 1.25, py: 1, bgcolor: '#F5F7F8', borderRadius: 1.5 }}>
          {etape.phrase}
        </Typography>
      )}
      {etape.visible === false && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Dossier absent de l'espace client : archivé sans eVisa reçu.
        </Alert>
      )}
      {etape.jalons.length > 0 && <Frise jalons={etape.jalons} />}

      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 0.25 }}>
        Mails de suivi :
      </Typography>
      {suivi.email && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25, overflowWrap: 'anywhere' }}>
          Destinataire : {suivi.email}
        </Typography>
      )}
      {journal.length === 0 ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Aucun mail de suivi pour l'instant.
        </Typography>
      ) : (
        <Box component="ul" aria-label="Mails de suivi" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {mailsVisibles.map((e, k) => (
            <LigneMail key={`${e.date_creation}-${k}`} entree={e} derniere={k === mailsVisibles.length - 1} />
          ))}
        </Box>
      )}
      {journal.length > MAX_MAILS_REPLIES && (
        <Button size="small" onClick={() => setTousLesMails((v) => !v)} sx={{ textTransform: 'none', mt: 0.25 }}>
          {tousLesMails ? 'Afficher seulement les plus récents' : `Afficher les ${journal.length - MAX_MAILS_REPLIES} plus anciens`}
        </Button>
      )}

      <Typography variant="body2" sx={{ mt: 2 }}>
        <strong>Dernière connexion à l'espace client :</strong>{' '}
        {suivi.derniere_connexion ? formaterDateSuivi(suivi.derniere_connexion) : 'jamais'}
      </Typography>
    </Paper>
  );
};

export default SuiviClientDossier;
